// Helpers shared by the enum field components. Option values keep their JSON
// type (string / number / boolean); the DOM only speaks strings, so we map
// to/from a stringified key and match values JSON-type-tolerantly (mirrors the
// validation engine's `valuesEqual`).

import { labelToText } from '../richText'
import type { DynamicFormField, Option } from '../types'

/** A field's own option list, when it has one. A field without inline options
 *  either takes free text or resolves its choices from a remote data source. */
export function inlineOptions(field: DynamicFormField): Option[] | undefined {
  const options = (field as { options?: unknown }).options
  return Array.isArray(options) ? (options as Option[]) : undefined
}

/** Stable string key for an option value, for use as a DOM <option value>. */
export function optionKey(value: unknown): string {
  return value == null ? '' : String(value)
}

/** JSON-type-tolerant equality (number 1 ≡ string "1"); objects by reference. */
export function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null || b == null) return false
  if (typeof a === 'object' || typeof b === 'object') return false
  return String(a) === String(b)
}

/**
 * Options to display: drop `active: false` entries unless the option is
 * currently selected, so legacy data referencing a now-inactive option still
 * renders. `isSelected` is called with each option's value.
 */
export function visibleOptions(
  options: Option[],
  isSelected: (value: unknown) => boolean,
): Option[] {
  return options.filter((opt) => opt.active !== false || isSelected(opt.value))
}

/** Options with labels flattened to text, for `<option>` and other plain-text contexts. */
export function withTextLabels(options: Option[]): Option[] {
  return options.map((opt) => ({ ...opt, label: labelToText(opt.label) }))
}

/** Find an option by its stringified key. */
export function optionByKey(options: Option[], key: string): Option | undefined {
  return options.find((opt) => optionKey(opt.value) === key)
}
